import { useOutletContext, useNavigate } from 'react-router-dom';
import { Card, Row, Col, Tag, Typography, Space, Button, Modal, message, Input } from 'antd';
import { ActivityCategoryInfo, ActivityOutletContext } from '../../interface';
import { deleteMatchActivityCategory, reloadMatchActivityCategory } from '../../apiBridge';
import '../../assets/Categories.css';

const { Text } = Typography;

const Categories: React.FC = () => {
    const { activity, categories, reloadData } = useOutletContext() as ActivityOutletContext;
    const navigate = useNavigate();
    const [messageApi, contextHolder] = message.useMessage();

    if (!categories) {
        return null;
    }

    const handleReload = async (category: ActivityCategoryInfo) => {
        Modal.confirm({
            title: 'Reload Category',
            content: 'This rebuilds the category for the current participant list. Existing matches, schedules, and the bracket draft are removed. Finish Category Setup again before creating a new draft.',
            okText: 'Confirm',
            cancelText: 'Cancel',
            onOk: async () => {
                const res = await reloadMatchActivityCategory(activity.id, category.id);
                if (res) {
                    const data = await res.json();
                    if (data.error) {
                        messageApi.error(data.error);
                    } else if (data.discarded_seed_locks > 0) {
                        messageApi.warning(
                            `Category reloaded. ${data.discarded_seed_locks} incompatible seed lock(s) were removed.`,
                        );
                        reloadData("category");
                    } else {
                        messageApi.success("Category reloaded successfully");
                        reloadData("category");
                    }
                }
            }
        });
    };

    const openBracketBuilder = (category: ActivityCategoryInfo) => {
        if (category.need_reload) {
            messageApi.warning("Participant information has changed. Please reload the category before editing the bracket.");
            return;
        }
        if (!category.is_setup_done) {
            messageApi.warning("Finish Category Setup before creating or editing the bracket draft.");
            return;
        }
        navigate(`/activity/${activity.id}/bracket-builder/${category.id}`);
    };

    const handleDeleteCategory = (category: ActivityCategoryInfo) => {
        let inputValue = '';
        Modal.confirm({
            title: 'Delete Category',
            content: (
                <div>
                    <p>Are you sure you want to delete category "{category.name}"?</p>
                    <p>This action cannot be undone. All related match data will be deleted.</p>
                    <p>Please enter the category name to confirm deletion:</p>
                    <Input 
                        placeholder="Enter category name" 
                        onChange={(e) => {
                            inputValue = e.target.value;
                        }}
                    />
                </div>
            ),
            okText: 'Confirm',
            cancelText: 'Cancel',
            onOk: () => {
                if (inputValue === category.name) {
                    Modal.confirm({
                        title: 'Final Confirmation',
                        content: 'Are you sure you want to delete this category? This action cannot be undone.',
                        okText: 'Delete',
                        cancelText: 'Cancel',
                        okButtonProps: { danger: true },
                        onOk: async () => {
                            const res = await deleteMatchActivityCategory(activity.id, category.id);
                            if (res) {
                                const data = await res.json();
                                if (data.error) {
                                    messageApi.error(data.error);
                                } else {
                                    messageApi.success("Category deleted successfully");
                                    reloadData("category");
                                }
                            }
                        }
                    });
                } else {
                    messageApi.error("Category name does not match");
                }
            }
        });
    };

    return (
        <>
            {contextHolder}
            <Row gutter={[16, 16]} className="category-list">
                {categories.map((category) => {
                    const bracketStatus = category.bracket_status ||
                        (category.is_bracket_done ? 'published' : 'not_created');
                    const bracketStatusLabel = bracketStatus === 'published'
                        ? 'Published'
                        : bracketStatus === 'draft'
                            ? 'Draft'
                            : 'Not Created';
                    const bracketStatusColor = bracketStatus === 'published'
                        ? 'success'
                        : bracketStatus === 'draft'
                            ? 'processing'
                            : 'warning';
                    const builderButtonLabel = bracketStatus === 'draft'
                        ? 'Continue Draft'
                        : 'Create Draft';

                    return (
                    <Col key={category.id} xs={24} md={12} xl={8}>
                        <Card
                            className="category-list-card"
                            title={category.name}
                            extra={
                                <Button 
                                    danger 
                                    type="link" 
                                    onClick={() => handleDeleteCategory(category)}
                                >
                                    Delete
                                </Button>
                            }
                        >
                            <Space direction="vertical" style={{ width: '100%' }}>
                                <div>
                                    <Text type="secondary">Participants: {category.participant_count}</Text>
                                </div>
                                <div className="category-status-row">
                                    <div className="category-status-summary">
                                        <Text className="category-status-label">Category Setup:</Text>
                                        <Tag color={category.is_setup_done ? 'success' : 'warning'}>
                                            {category.is_setup_done ? 'Complete' : 'Pending'}
                                        </Tag>
                                    </div>

                                    <div className="category-status-actions">
                                        <Button 
                                            size="small" 
                                            disabled={category.need_reload}
                                            onClick={() => navigate(`/activity/${activity.id}/category/${category.id}`)}
                                        >
                                            Edit
                                        </Button>
                                    </div>
                                </div>

                                <div className="category-status-row">
                                    <div className="category-status-summary">
                                        <Text className="category-status-label">Schedule:</Text>
                                        <Tag color={category.is_schedule_done ? 'success' : 'warning'}>
                                            {category.is_schedule_done ? 'Complete' : 'Pending'}
                                        </Tag>
                                    </div>

                                    <div className="category-status-actions">
                                        <Button
                                            size="small"
                                            disabled={category.need_reload || !category.is_setup_done}
                                            onClick={() => navigate(`/activity/${activity.id}/schedule?category=${category.id}`)}
                                        >
                                            Edit
                                        </Button>
                                    </div>
                                </div>

                                <div className="category-status-row">
                                    <div className="category-status-summary">
                                        <Text className="category-status-label">Bracket:</Text>
                                        <Tag color={bracketStatusColor}>
                                            {bracketStatusLabel}
                                        </Tag>
                                    </div>

                                    <div className="category-status-actions">
                                        {bracketStatus !== 'published' && (
                                            <Button
                                                size="small"
                                                onClick={() => openBracketBuilder(category)}
                                                disabled={category.need_reload || !category.is_setup_done}
                                            >
                                                {builderButtonLabel}
                                            </Button>
                                        )}

                                        <Button
                                            size="small"
                                            disabled={category.need_reload || !category.is_setup_done}
                                            onClick={() => navigate(`/activity/${activity.id}/bracket?category=${category.id}`)}
                                        >
                                            Bracket
                                        </Button>
                                    </div>
                                </div>

                                {category.need_reload && (
                                    <div className="category-status-row">
                                        <Text type="warning">Participant info changed</Text>
                                        <Button
                                            type="primary"
                                            onClick={() => handleReload(category)}
                                        >
                                            Reload
                                        </Button>
                                    </div>
                                )}
                            </Space>
                        </Card>
                    </Col>
                    );
                })}
            </Row>
        </>
    );
};

export default Categories;
